import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { LogoFull } from "@/components/Logo";
import { LogOut, RefreshCw, Trash2, Phone, Clock, UserCheck, CheckCheck, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const STATUS_STYLES = {
  new: { label: "New", cls: "border-[#C85A32]/40 bg-[#C85A32]/10 text-[#C85A32]" },
  contacted: { label: "Contacted", cls: "border-[#D4A359]/50 bg-[#D4A359]/15 text-[#8A6524]" },
  done: { label: "Done", cls: "border-[#1C3B2B]/40 bg-[#1C3B2B]/10 text-[#1C3B2B]" },
};

const inputCls =
  "w-full rounded-2xl border border-[#E4DDD3] bg-white px-5 py-3.5 text-sm text-[#1A1C18] outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-[#7B867C]/70 focus:border-[#1C3B2B] focus:ring-4 focus:ring-[#1C3B2B]/10";

export default function Admin() {
  const [auth, setAuth] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadQuotes = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/quotes`, { withCredentials: true });
    setQuotes(data);
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setAuth(r.data);
        loadQuotes().catch(() => {});
      })
      .catch(() => setAuth(false));
  }, [loadQuotes]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setAuth(data);
      await loadQuotes();
      toast.success("Welcome back!");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setAuth(false);
    toast.success("Logged out");
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await loadQuotes();
    } finally {
      setRefreshing(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, { withCredentials: true });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Marked as ${STATUS_STYLES[status].label.toLowerCase()}`);
    } catch {
      toast.error("Could not update. Try again.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, { withCredentials: true });
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Quote request deleted");
    } catch {
      toast.error("Could not delete. Try again.");
    }
  };

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FBF9F5]">
        <p className="font-jbm text-xs uppercase tracking-[0.3em] text-[#7B867C]">Loading…</p>
      </div>
    );
  }

  if (auth === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FBF9F5] px-5">
        <Toaster position="top-center" richColors />
        <div className="w-full max-w-md rounded-3xl border border-[#E4DDD3] bg-white p-8 shadow-[0_35px_70px_-35px_rgba(28,59,43,0.4)] sm:p-10">
          <LogoFull />
          <h1 className="font-display mt-8 text-2xl font-bold text-[#1C3B2B]">Owner Login</h1>
          <p className="mt-2 text-sm text-[#525B53]">Sign in to see incoming quote requests.</p>
          <form data-testid="admin-login-form" onSubmit={login} className="mt-7 space-y-5">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-[#525B53]">Email</label>
              <input
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-[#525B53]">Password</label>
              <input
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={inputCls}
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="rounded-xl bg-[#C85A32]/10 px-4 py-3 text-sm font-medium text-[#C85A32]">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-[#1C3B2B] py-4 text-sm font-semibold text-[#FBF9F5] transition-[background-color] duration-300 hover:bg-[#132A1F] disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <a href="/" className="mt-6 block text-center text-sm text-[#7B867C] transition-colors hover:text-[#1C3B2B]">
            ← Back to website
          </a>
        </div>
      </div>
    );
  }

  const counts = {
    new: quotes.filter((q) => (q.status || "new") === "new").length,
    contacted: quotes.filter((q) => q.status === "contacted").length,
    done: quotes.filter((q) => q.status === "done").length,
  };

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-40 border-b border-[#E4DDD3]/60 bg-[#FBF9F5]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 sm:px-8">
          <a href="/" data-testid="admin-home-link" className="transition-transform duration-300 hover:scale-[1.03]">
            <LogoFull />
          </a>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={refresh}
              className="flex items-center gap-2 rounded-full border border-[#E4DDD3] bg-white px-4 py-2.5 text-sm font-semibold text-[#1C3B2B] transition-colors hover:bg-[#F3EDE2]"
            >
              <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-[#1C3B2B] px-4 py-2.5 text-sm font-semibold text-[#FBF9F5] transition-colors hover:bg-[#132A1F]"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main data-testid="admin-dashboard" className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1C3B2B]">Quote Requests</h1>
        <p className="mt-2 text-sm text-[#525B53]">Signed in as {auth.email}</p>

        <div className="mt-8 grid grid-cols-3 gap-4">
          {[
            { key: "new", icon: Inbox, label: "New" },
            { key: "contacted", icon: UserCheck, label: "Contacted" },
            { key: "done", icon: CheckCheck, label: "Done" },
          ].map((s) => (
            <div key={s.key} data-testid={`admin-stat-${s.key}`} className="rounded-2xl border border-[#E4DDD3] bg-white p-5 shadow-sm">
              <s.icon size={18} className="text-[#C85A32]" />
              <p className="font-display mt-3 text-2xl font-bold text-[#1C3B2B] sm:text-3xl">{counts[s.key]}</p>
              <p className="text-xs font-medium uppercase tracking-[0.14em] text-[#7B867C]">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 space-y-4">
          {quotes.length === 0 && (
            <p data-testid="admin-empty-state" className="rounded-2xl border border-dashed border-[#E4DDD3] bg-white p-10 text-center text-sm text-[#7B867C]">
              No quote requests yet. New submissions from the website will appear here.
            </p>
          )}
          {quotes.map((q) => {
            const st = STATUS_STYLES[q.status || "new"];
            return (
              <div key={q.id} data-testid={`quote-card-${q.id}`} className="rounded-2xl border border-[#E4DDD3] bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="font-display text-lg font-bold text-[#1A1C18]">{q.name}</h2>
                      <span className={`rounded-full border px-3 py-1 font-jbm text-[10px] font-semibold uppercase tracking-[0.14em] ${st.cls}`}>
                        {st.label}
                      </span>
                    </div>
                    <a href={`tel:${q.phone.replace(/\s/g, "")}`} className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold text-[#1C3B2B] hover:underline">
                      <Phone size={13} /> {q.phone}
                    </a>
                    <p className="mt-1 text-sm text-[#525B53]">
                      {q.location} · {q.property_type === "home" ? "Home / Villa" : "Business / Commercial"}
                    </p>
                    {q.services?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {q.services.map((s) => (
                          <span key={s} className="rounded-full bg-[#F3EDE2] px-3 py-1 text-xs font-medium text-[#1C3B2B]">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                    {q.details && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#525B53]">"{q.details}"</p>}
                    <p className="mt-3 flex items-center gap-1.5 text-xs text-[#7B867C]">
                      <Clock size={12} /> {new Date(q.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    {q.status !== "contacted" && q.status !== "done" && (
                      <button
                        data-testid={`quote-${q.id}-contacted-button`}
                        onClick={() => setStatus(q.id, "contacted")}
                        className="rounded-full border border-[#D4A359]/60 px-4 py-2 text-xs font-semibold text-[#8A6524] transition-colors hover:bg-[#D4A359]/15"
                      >
                        Mark Contacted
                      </button>
                    )}
                    {q.status !== "done" && (
                      <button
                        data-testid={`quote-${q.id}-done-button`}
                        onClick={() => setStatus(q.id, "done")}
                        className="rounded-full border border-[#1C3B2B]/40 px-4 py-2 text-xs font-semibold text-[#1C3B2B] transition-colors hover:bg-[#1C3B2B]/10"
                      >
                        Mark Done
                      </button>
                    )}
                    <button
                      data-testid={`quote-${q.id}-delete-button`}
                      onClick={() => remove(q.id)}
                      className="flex items-center justify-center gap-1.5 rounded-full border border-[#C85A32]/40 px-4 py-2 text-xs font-semibold text-[#C85A32] transition-colors hover:bg-[#C85A32]/10"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
